import { useState, useEffect } from 'react';
import { fetchInactiveFlavorRequest } from '../services/flavorApi';

/**
 * Hook to check for pending inactive flavor requests
 * Polls the API every 30 minutes
 * @returns {Object} { hasPendingRequest, pendingCount }
 */
export const useInactiveRequestNotification = (isAdmin) => {
    const [hasPendingRequest, setHasPendingRequest] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [requestedByName, setRequestedByName] = useState(null);

    useEffect(() => {
        if (!isAdmin) {
            return;
        }

        const checkForPendingRequests = async () => {
            try {
                const response = await fetchInactiveFlavorRequest();

                if (Array.isArray(response) && response.length > 0) {
                    // Filter for requests that are not approved yet
                    const pendingRequests = response.filter(req => !req.approved_at);
                    setHasPendingRequest(pendingRequests.length > 0);
                    setPendingCount(pendingRequests.length);
                    setRequestedByName(pendingRequests[0]?.requested_by_name || null);
                } else {
                    setHasPendingRequest(false);
                    setPendingCount(0);
                    setRequestedByName(null);
                }
            } catch (error) {
                setHasPendingRequest(false);
                setPendingCount(0);
                setRequestedByName(null);
            }
        };

        // Check immediately on mount
        checkForPendingRequests();

        // Then check every 30 minutes (1800000 ms)
        const interval = setInterval(checkForPendingRequests, 1800000);

        // Listen for custom event to refetch immediately
        const handleRefetch = () => {
            checkForPendingRequests();
        };
        window.addEventListener('inactiveRequestExecuted', handleRefetch);

        return () => {
            clearInterval(interval);
            window.removeEventListener('inactiveRequestExecuted', handleRefetch);
        };
    }, [isAdmin]);

    return { hasPendingRequest, pendingCount, requestedByName };
};
